import React from 'react';
import { getDashboard } from '../../../../../../helpers/front-helper';
import { getCustomer } from '../../../../../../helpers/customer-helper';
import { getProject } from '../../../../../../helpers/project-helper';
import {
    createEmptyDashboard,
    QUICK_RANGE_OPTIONS,
} from '../helpers';

export const useHomeDashboard = ({ token }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingFilters, setIsLoadingFilters] = React.useState(false);
    const [error, setError] = React.useState('');

    const [customers, setCustomers] = React.useState([]);
    const [projects, setProjects] = React.useState([]);

    const [days, setDays] = React.useState(30);
    const [customerId, setCustomerId] = React.useState('');
    const [projectId, setProjectId] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = React.useState(false);

    const [dashboard, setDashboard] = React.useState(createEmptyDashboard());

    const quickRangeOptions = React.useMemo(() => QUICK_RANGE_OPTIONS, []);

    const loadFilters = React.useCallback(async () => {
        if (!token) {
            return;
        }

        setIsLoadingFilters(true);

        const [customersResp, projectsResp] = await Promise.all([
            getCustomer(token, { limit: 500, offset: 0 }),
            getProject(token, { limit: 500, offset: 0 }),
        ]);

        if (customersResp?.success) {
            setCustomers(Array.isArray(customersResp.data) ? customersResp.data : []);
        }

        if (projectsResp?.success) {
            setProjects(Array.isArray(projectsResp.data) ? projectsResp.data : []);
        }

        setIsLoadingFilters(false);
    }, [token]);

    const loadDashboard = React.useCallback(async () => {
        if (!token) {
            setError('No se pudo validar la sesión.');
            return;
        }

        setIsLoading(true);
        setError('');

        const params = {
            days,
        };

        if (customerId) {
            params.customerId = customerId;
        }

        if (projectId) {
            params.projectId = projectId;
        }

        if (dateFrom) {
            params.dateFrom = dateFrom;
        }

        if (dateTo) {
            params.dateTo = dateTo;
        }

        const resp = await getDashboard(token, params);
        if (!resp?.success) {
            setError(resp?.message || 'No se pudo obtener la información del dashboard.');
            setIsLoading(false);
            return;
        }

        setDashboard(resp?.data || createEmptyDashboard());
        setIsLoading(false);
    }, [token, days, customerId, projectId, dateFrom, dateTo]);

    React.useEffect(() => {
        loadFilters();
    }, [loadFilters]);

    React.useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const customerOptions = React.useMemo(
        () => [{ value: '', label: 'Todos' }, ...customers.map((customer) => ({ value: customer._id, label: customer.name }))],
        [customers]
    );

    const projectOptions = React.useMemo(
        () => [{ value: '', label: 'Todos' }, ...projects.map((project) => ({ value: project._id, label: project.name }))],
        [projects]
    );

    const hasActiveFilters = Boolean(dateFrom || dateTo || customerId || projectId || Number(days) !== 30);

    const trendSeries = dashboard?.tickets?.createdTrend || [];
    const maxTrendValue = trendSeries.reduce((max, item) => Math.max(max, Number(item.total || 0)), 0);
    const trendPoints = trendSeries
        .map((item, index) => {
            const x = trendSeries.length <= 1 ? 0 : (index / (trendSeries.length - 1)) * 100;
            const y = maxTrendValue <= 0 ? 100 : 100 - ((Number(item.total || 0) / maxTrendValue) * 100);
            return `${x},${y}`;
        })
        .join(' ');

    const statusTotal = (dashboard?.tickets?.byStatus || []).reduce((accumulator, item) => accumulator + Number(item.total || 0), 0);
    const priorityTotal = (dashboard?.tickets?.byPriority || []).reduce((accumulator, item) => accumulator + Number(item.total || 0), 0);
    const typeTotal = (dashboard?.tickets?.byType || []).reduce((accumulator, item) => accumulator + Number(item.total || 0), 0);

    const getVariation = (currentValue, previousValue) => {
        const current = Number(currentValue || 0);
        const previous = Number(previousValue || 0);

        if (previous === 0) {
            if (current === 0) {
                return 0;
            }
            return 100;
        }

        return ((current - previous) / previous) * 100;
    };

    const variationTickets = getVariation(dashboard?.summary?.tickets, dashboard?.previousSummary?.tickets);
    const variationOpen = getVariation(dashboard?.summary?.openTickets, dashboard?.previousSummary?.openTickets);
    const variationClosed = getVariation(dashboard?.summary?.closedTickets, dashboard?.previousSummary?.closedTickets);

    const formatNumber = React.useCallback((value) => Number(value || 0).toLocaleString('es-MX'), []);

    const formatVariation = React.useCallback((value) => {
        const numeric = Number(value || 0);
        const sign = numeric > 0 ? '+' : '';
        return `${sign}${numeric.toFixed(1)}%`;
    }, []);

    const variationClass = React.useCallback((value) => {
        if (value > 0) {
            return 'text-success';
        }

        if (value < 0) {
            return 'text-danger';
        }

        return 'text-muted';
    }, []);

    const handleClearFilters = React.useCallback(() => {
        setDays(30);
        setDateFrom('');
        setDateTo('');
        setCustomerId('');
        setProjectId('');
    }, []);

    return {
        isLoading,
        isLoadingFilters,
        isBusy: isLoading || isLoadingFilters,
        error,
        dashboard,
        days,
        setDays,
        customerId,
        setCustomerId,
        projectId,
        setProjectId,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        isFiltersDrawerOpen,
        setIsFiltersDrawerOpen,
        quickRangeOptions,
        customerOptions,
        projectOptions,
        hasActiveFilters,
        trendSeries,
        trendPoints,
        statusTotal,
        priorityTotal,
        typeTotal,
        variationTickets,
        variationOpen,
        variationClosed,
        formatNumber,
        formatVariation,
        variationClass,
        handleClearFilters,
        loadDashboard,
    };
};
